import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import BasicButton from "../../common/components/BasicButton";
import Input from "../../common/components/Input";
import { formatDate } from "../../../utilities/format";
import { Document } from "./DocumentTable";
import {
  ListBulletIcon,
  CheckCircleIcon,
  MinusIcon,
  PlusIcon,
  RectangleStackIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ArrowPathIcon
} from "@heroicons/react/24/outline";

interface GenerationHistoryItem {
  id: string;
  status: string;
  generatedCount: number;
  requestedAt: string;
  finishedAt?: string | null;
}

interface DifficultyData {
  id: string;
  code: string;
  label: string;
  points: number;
  order: number;
}

interface DocumentDetailProps {
  document: Document;
  onSaveTitle?: (documentId: string, newTitle: string) => void;
  onDownload?: (documentId: string) => void;
  onGenerateQuizzes?: (mcqCount: number, oxCount: number, shortCount: number, difficulty: string) => void;
  onRefresh?: () => void;
  difficulties?: DifficultyData[];
  generationHistory?: GenerationHistoryItem[];
}

interface QuizType {
  id: string;
  name: string;
  icon: React.ReactNode;
  count: number;
}

export default function DocumentDetail({ document, onSaveTitle, onDownload, onGenerateQuizzes, onRefresh, difficulties = [], generationHistory = [] }: DocumentDetailProps) {
  const navigate = useNavigate();
  const [editTitle, setEditTitle] = useState(document.title);
  const hasChanges = editTitle !== document.title;

  // Status translation map
  const statusMap: { [key: string]: string } = {
    'NEW': '신규',
    'DRAFT_REQUESTED': '생성중',
    'DRAFT_REQUEST_FAILED': '생성 실패',
    'DRAFTED': '생성됨',
    'SELECTED': '선택됨',
    'REJECTED': '거부됨',
    'ON_HOLD': '보류됨',
    'DELETED': '삭제됨',
  };

  const getStatusLabel = (status: string) => {
    return statusMap[status] || status;
  };

  const [quizTypes, setQuizTypes] = useState<QuizType[]>([
    {
      id: "multiple",
      name: "객관식 유형",
      icon: (
        <div className="w-5 h-5 flex items-center justify-center">
          <ListBulletIcon className="h-5 w-5 text-blue-600" />
        </div>
      ),
      count: 0,
    },
    {
      id: "ox",
      name: "OX 유형",
      icon: (
        <div className="w-5 h-5 flex items-center justify-center">
          <CheckCircleIcon className="h-5 w-5 text-blue-600" />
        </div>
      ),
      count: 0,
    },
    {
      id: "short",
      name: "단답형",
      icon: (
        <div className="w-4 h-4 border border-blue-600 rounded flex items-center justify-center">
          <MinusIcon className="h-2.5 w-2.5 text-blue-600" />
        </div>
      ),
      count: 0,
    },
  ]);

  const [selectedDifficulty, setSelectedDifficulty] = useState<string>(difficulties[0]?.id || "");
  const [expandedHistory, setExpandedHistory] = useState<number[]>([]);


  const handleSave = () => {
    onSaveTitle?.(document.documentId, editTitle);
  };

  const handleCancel = () => {
    setEditTitle(document.title);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleDownload = () => {
    onDownload?.(document.documentId);
  };

  const handleGenerateQuizzes = () => {
    const mcqCount = quizTypes.find(t => t.id === "multiple")?.count || 0;
    const oxCount = quizTypes.find(t => t.id === "ox")?.count || 0;
    const shortCount = quizTypes.find(t => t.id === "short")?.count || 0;
    const selectedDifficultyData = difficulties.find(d => d.id === selectedDifficulty);
    const difficulty = selectedDifficultyData?.label || "적당함";

    onGenerateQuizzes?.(mcqCount, oxCount, shortCount, difficulty);

    // Reset quiz types and difficulty after generation
    setQuizTypes(prev => prev.map(type => ({ ...type, count: 0 })));
    setSelectedDifficulty(difficulties[0]?.id || "");
  };

  const updateQuizTypeCount = (typeId: string, increment: boolean) => {
    setQuizTypes(prev =>
      prev.map(type =>
        type.id === typeId
          ? { ...type, count: Math.max(0, type.count + (increment ? 1 : -1)) }
          : type
      )
    );
  };

  const toggleHistoryExpansion = (historyId: number) => {
    setExpandedHistory(prev =>
      prev.includes(historyId)
        ? prev.filter(id => id !== historyId)
        : [...prev, historyId]
    );
  };

  return (
    <div className="flex gap-6">
      {/* 문서 상세 영역 */}
      <div className="bg-white rounded-lg p-6 border border-gray-200 flex-1" style={{ minWidth: '800px' }}>
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 transition-colors"
          >
            <ChevronLeftIcon className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-2xl font-bold">문서 상세</h1>
        </div>

        <div className="space-y-6 w-full">
          {/* 생성자 */}
          <div className="flex items-center w-full">
            <div className="text-sm font-medium text-gray-700 w-24 sm:w-32 lg:w-40 flex-shrink-0">
              생성자
            </div>
            <div className="text-sm text-gray-900 flex-1 ml-4">
              {document.creator}
            </div>
          </div>

          {/* 생성일시 */}
          <div className="flex items-center w-full">
            <div className="text-sm font-medium text-gray-700 w-24 sm:w-32 lg:w-40 flex-shrink-0">
              생성일시
            </div>
            <div className="text-sm text-gray-900 flex-1 ml-4">
              {formatDate(document.createdAt, "YYYY.MM.DD HH:mm")}
            </div>
          </div>

          {/* 수정일시 */}
          <div className="flex items-center w-full">
            <div className="text-sm font-medium text-gray-700 w-24 sm:w-32 lg:w-40 flex-shrink-0">
              수정일시
            </div>
            <div className="text-sm text-gray-900 flex-1 ml-4">
              {formatDate(document.updatedAt, "YYYY.MM.DD HH:mm")}
            </div>
          </div>

          {/* 문서명 */}
          <div className="space-y-3 w-full">
            <div className="flex items-center justify-between w-full">
              <div className="text-sm font-medium text-gray-700 w-24 sm:w-32 lg:w-40 flex-shrink-0">
                문서명
              </div>
              <BasicButton
                text="다운로드"
                variant="text"
                className="text-sm py-1 px-3 underline flex-shrink-0"
                onClick={handleDownload}
              />
            </div>
            <div className="space-y-3 ml-0 sm:ml-4">
              <Input
                value={editTitle}
                onValueChange={setEditTitle}
                className="w-full max-w-none bg-white"
              />
              <div className="flex justify-end gap-2 w-full">
                <BasicButton
                  text="저장"
                  variant="primary"
                  className="text-xs py-1 px-3 h-8"
                  onClick={handleSave}
                  disabled={!hasChanges}
                />
                <BasicButton
                  text="취소"
                  variant="outlined"
                  className="text-xs py-1 px-3 h-8"
                  onClick={handleCancel}
                  disabled={!hasChanges}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 퀴즈 생성 섹션 */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h2 className="text-2xl font-bold mb-6">퀴즈 생성</h2>

          {/* 유형 별 개수 설정 */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">유형 별 개수 설정</h3>
            <div className="space-y-4">
              {quizTypes.map((type) => (
                <div
                  key={type.id}
                  className={`flex items-center justify-between w-full p-4 rounded-lg border-2 transition-colors ${
                    type.count > 0
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-300 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {type.icon}
                    <span className="text-sm font-medium text-gray-900">
                      {type.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => updateQuizTypeCount(type.id, false)}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                      disabled={type.count === 0}
                    >
                      <MinusIcon className="h-4 w-4 text-gray-600" />
                    </button>
                    <span className="w-8 text-center text-sm font-semibold">
                      {type.count}
                    </span>
                    <button
                      onClick={() => updateQuizTypeCount(type.id, true)}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                    >
                      <PlusIcon className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 난이도 설정 */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">난이도 설정</h3>
            <div className="space-y-6">
              <div className="flex items-center w-full">
                <div className="text-sm font-medium text-gray-700 w-24 sm:w-32 lg:w-40 flex-shrink-0">
                  획득 포인트
                </div>
                <div className="text-sm text-gray-900 flex-1 ml-4">
                  {difficulties.find(d => d.id === selectedDifficulty)?.points || 0}
                </div>
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              {difficulties.map((difficulty) => (
                <div
                  key={difficulty.id}
                  onClick={() => setSelectedDifficulty(difficulty.id)}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors flex-1 ${
                    selectedDifficulty === difficulty.id
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-300 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="w-6 h-6 rounded border-2 border-blue-600 bg-blue-600 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">
                      {difficulty.order}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {difficulty.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 버튼 영역 */}
          <div className="flex justify-end gap-3 mt-8">
            <BasicButton
              text="목록으로"
              variant="outlined"
              className="px-6 py-2 border-black text-black hover:bg-gray-100"
            />
            <BasicButton
              text="퀴즈 생성"
              variant="primary"
              className="px-6 py-2 bg-black text-white hover:bg-gray-800"
              onClick={handleGenerateQuizzes}
            />
          </div>
        </div>
      </div>

      {/* 퀴즈 생성내역 영역 */}
      <div className="bg-white rounded-lg p-6 border border-gray-200 flex-1">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold">퀴즈 생성내역</h2>
            <span className="text-gray-500">(총 {generationHistory.length}회)</span>
          </div>
          <button
            onClick={onRefresh}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            title="새로고침"
          >
            <ArrowPathIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        <div className="space-y-4">
          {generationHistory.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              생성 내역이 없습니다.
            </div>
          ) : (
            generationHistory.map((history) => {
              const isExpanded = expandedHistory.includes(Number(history.id));
              return (
                <div key={history.id} className="border border-gray-200 rounded-lg p-4">
                  {/* 기본 정보 */}
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold text-black">
                            생성번호 : {history.id}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            상태: {getStatusLabel(history.status)}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {history.status === 'DRAFTED' && (
                            <BasicButton
                              text="퀴즈확인"
                              variant="text"
                              className="text-sm py-1 px-2 underline"
                              onClick={() => navigate(`/quiz/management?searchField=생성번호&searchQuery=${history.id}`)}
                            />
                          )}
                          <button
                            onClick={() => toggleHistoryExpansion(Number(history.id))}
                            className="p-1"
                          >
                            {isExpanded ? (
                              <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                            ) : (
                              <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 확장된 정보 */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">생성된 퀴즈 수</span>
                        <span className="font-bold text-black">{history.generatedCount}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">요청일시</span>
                        <span className="font-bold text-black">
                          {formatDate(history.requestedAt, "YYYY.MM.DD HH:mm")}
                        </span>
                      </div>
                      {history.finishedAt && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">완료일시</span>
                          <span className="font-bold text-black">
                            {formatDate(history.finishedAt, "YYYY.MM.DD HH:mm")}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}